import React from "react";
import { getLogicalName } from "services/ApiService";
import {
  AllocationItem,
  CPCode,
  CustomPropertyProps,
  ICustomProperties,
  IDependencyField,
  TDefaultValue,
} from "./types";
import { getNextKey } from "app/utils";

export const getObjectLogicalName = (props: Partial<CustomPropertyProps>) => {
  if (props.isZNR) return "ExpenseApplication";
  if (props.isAO) return "AdvanceReportApplication";
  if (props.isExpense) return "Expense";
  if (props.isTransaction) return "Transaction";
  return "";
};

/**
 * Gets the object logical name id, given the props of the component.
 *
 * @param props The props of the component.
 * @param setObjectLogicalNameId The function to set the object logical name id.
 * @returns The object logical name id.
 */
export const getObjectLogicalId = async (
  props: Partial<CustomPropertyProps>,
  setObjectLogicalNameId?: React.Dispatch<React.SetStateAction<number>>
) => {
  const objectLogicalName = getObjectLogicalName(props);

  let objectLogicalNameId = 0;

  try {
    const resultON = await getLogicalName({
      params: { NameEn: objectLogicalName },
    });
    if (resultON.headers.success) {
      objectLogicalNameId = resultON.data[0].id;
      setObjectLogicalNameId?.(objectLogicalNameId);
    }
  } catch (e) {
    console.log("Ошибка при попытке получения objectLogicalId: ", e);
  }
  return objectLogicalNameId;
};

/**
 * Converts TDefaultValue to a select option data type
 * @param value - a TDefaultValue object
 * @param customPropertyId - a custom property id
 * @param customPropertyCode - a custom property code
 * @param lang - a language
 * @returns a select option data type
 */
export const convertSelectDefaultValue = (
  value: IDependencyField,
  customPropertyId: number,
  customPropertyCode: string,
  lang: string
) => {
  const name = { ru: value.nameRu, en: value.nameEn };
  return {
    value: value.dependentCustomPropertyListValueId,
    percent: value?.percent,
    text: name[lang as "ru" | "en"],
    customPropertyId: customPropertyId,
    customPropertyCode: customPropertyCode,
  };
};

/**
 * Converts an array of SelectOptionData objects into a format suitable for select inputs.
 *
 * @param {SelectOptionData[]} data - The array of data objects to be converted.
 * @param {string} lang - The language code ('ru' or 'en') to determine the text field.
 * @returns {Array} An array of objects with the fields: value, text, customPropertyId, and customPropertyCode.
 */
export const convertSelectOptionData = (
  data: TDefaultValue,
  customPropertyId: string,
  customPropertyCode: string,
  lang: string,
  key?: string,
  parentKey?: string
) => {
  const name = { ru: data?.nameRu, en: data?.nameEn };
  if (data && (name[lang as "ru" | "en"] || !!data.listValueId)) {
    return {
      value: data?.listValueId,
      percent: data?.percent,
      text: name[lang as "ru" | "en"],
      customPropertyId: customPropertyId,
      customPropertyCode: customPropertyCode,
      key: key || "",
      parentKey: parentKey || "",
    };
  } else return null;
};

export const convertAllocationCodesToArray = (
  codes: CPCode[],
  lang: string
): AllocationItem[] => {
  return codes.map((code) => ({
    id: `alloc-${code.id}`,
    value: code.listValueId,
    name: lang === "ru" ? code.nameRu : code.nameEn,
    percent: code.percent || 0,
    validSelect: true,
    validPercent: true,
    text: lang === "ru" ? code.nameRu : code.nameEn,
  }));
};
